import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DataService } from '../../Service/data.service';
import { MainService } from '../../Service/main.service';
import { isAllowedVendorReport } from '../../shared/constants/vendor-reports.constants';
import { GlobalSearchAction, GlobalSearchItem } from './global-search.models';

@Injectable({ providedIn: 'root' })
export class GlobalSearchActionsService {
  constructor(
    private router: Router,
    private ngxLoader: NgxUiLoaderService,
    private dataService: DataService,
    private mainService: MainService
  ) {}

  runPrimary(item: GlobalSearchItem) {
    if (!item) {
      return;
    }
    const primary = (item.actions || []).find((a) => a.primary) || (item.actions || [])[0];
    if (primary) {
      this.run(primary, item);
      return;
    }
    if (item.route && item.route.path) {
      this.navigate(item.route.path, item.route.query);
      return;
    }
    this.runByType(item);
  }

  run(action: GlobalSearchAction, item?: GlobalSearchItem) {
    if (!action) {
      if (item) {
        this.runPrimary(item);
      }
      return;
    }
    const payload = action.payload || {};
    const entity = (item && item.entity) || {};

    const applicationId = payload.applicationId || payload.applicationID || payload.id || entity.applicationId || entity.applicationID || (item && item.id);
    const subscriberId = payload.subscriberId || payload.subscriberID || entity.subscriberId || entity.subscriberID;
    const applicantId = payload.applicantId || payload.applicantID || entity.applicantId || entity.applicantID;
    const searchName = payload.searchName || entity.searchName || (item && item.title) || '';

    switch (action.id) {
      case 'navigate':
        this.navigate(
          payload.path || (item && item.route && item.route.path),
          payload.query || (item && item.route && item.route.query)
        );
        break;

      case 'openQa':
      case 'openQaError':
      case 'openQaCompleted':
        if (this.isSubscriberSession()) {
          this.openSubscriberReportList(applicationId);
        } else {
          const fallback = action.id === 'openQaError' ? '/qa/errors'
            : (action.id === 'openQaCompleted' || this.isCompletedStatus(item)) ? '/qa/completed' : '/qa';
          this.openInQueue(applicationId, fallback);
        }
        break;

      case 'reorderErrors':
        if (this.isSubscriberSession()) {
          this.openSubscriberReportList(applicationId);
        } else {
          this.navigate('/qa/errors', {
            gsId: applicationId,
            openReorder: action.id === 'reorderErrors' ? 1 : undefined
          });
        }
        break;

      case 'viewReport':
      case 'openReport':
      case 'openReportContext':
      case 'viewReportContext':
      case 'viewReportApplicant':
        this.openReportViewer(applicationId, subscriberId, applicantId, searchName);
        break;

      case 'viewGroupReport':
        this.openGroupReportViewer(applicationId, subscriberId, applicantId);
        break;

      case 'editSubscriber':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber_edit_profile');
        } else if (subscriberId) {
          this.navigate('/add-subscriber', { subscriberId });
        } else {
          this.navigate('/subscribers/manage-subscriber', { searchKey: payload.companyName || entity.companyName || (item && item.title) });
        }
        break;

      case 'manageSubscriber':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber_edit_profile');
        } else {
          this.navigate('/subscribers/manage-subscriber', {
            searchKey: subscriberId || payload.companyName || entity.companyName || (item && item.title)
          });
        }
        break;

      case 'switchSubscriber':
        this.switchSubscriber(subscriberId, payload.companyName || entity.companyName);
        break;

      case 'openProperty':
        this.openProperty(
          payload.propertyId || entity.propertyId || payload.id,
          subscriberId,
          payload.companyName || entity.companyName || (item && item.title)
        );
        break;

      case 'openScreening':
        this.navigate('/add-subscriber', {
          subscriberId: subscriberId,
          screen: 'screening',
          id: payload.screeningCriteriaId || payload.id
        });
        break;

      case 'openPricing':
        this.navigate('/add-subscriber', {
          subscriberId: subscriberId,
          screen: 'pricing',
          id: payload.pricingCriteriaId || payload.id
        });
        break;

      case 'openInvoice':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber-manage-payment', {
            invoiceId: payload.invoiceId || entity.invoiceId || payload.id
          });
        } else {
          this.navigate('/invoice-list', {
            invoiceId: payload.invoiceId || entity.invoiceId || payload.id
          });
        }
        break;

      case 'returnToQa':
        this.returnToQa(payload, item);
        break;

      default:
        if (item && item.route && item.route.path) {
          this.navigate(item.route.path, item.route.query);
        } else if (item) {
          this.runByType(item);
        }
        break;
    }
  }

  runByType(item: GlobalSearchItem) {
    if (!item) {
      return;
    }
    const entity = item.entity || {};
    const id = entity.applicationId || entity.id || item.id;

    switch (item.type) {
      case 'application':
      case 'applicant':
        if (this.isSubscriberSession()) {
          this.openSubscriberReportList(entity.applicationId || id);
        } else {
          this.openInQueue(id, this.isCompletedStatus(item) ? '/qa/completed' : '/qa');
        }
        break;

      case 'report':
        this.openReportViewer(
          entity.applicationId || id,
          entity.subscriberId,
          entity.applicantId,
          entity.searchName || item.title
        );
        break;

      case 'subscriber':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber_edit_profile');
        } else if (entity.subscriberId || id) {
          this.navigate('/add-subscriber', { subscriberId: entity.subscriberId || id });
        } else {
          this.navigate('/subscribers/manage-subscriber', { searchKey: item.title });
        }
        break;

      case 'property':
        this.openProperty(entity.propertyId || id, entity.subscriberId, entity.companyName || item.title);
        break;

      case 'user':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber_users/managesubscriberuser', { searchKey: entity.searchKey || item.title });
        } else {
          this.navigate('/users/manageusers', { searchKey: entity.searchKey || item.title });
        }
        break;

      case 'invoice':
        if (this.isSubscriberSession()) {
          this.navigate('/subscriber-manage-payment', { invoiceId: entity.invoiceId || id });
        } else {
          this.navigate('/invoice-list', { invoiceId: entity.invoiceId || id });
        }
        break;

      case 'queue':
        if (this.isSubscriberSession()) {
          this.openSubscriberReportList(entity.applicationId || id);
        } else {
          this.navigate(item.route?.path || '/qa', item.route?.query || { name: id, applicationId: id });
        }
        break;

      case 'page':
        this.navigate(item.route?.path || '/home', item.route?.query);
        break;

      default:
        if (item.route?.path) {
          this.navigate(item.route.path, item.route.query);
        }
        break;
    }
  }

  openReportViewer(applicationId: any, subscriberId?: any, applicantId?: any, searchName?: string) {
    if (!applicationId) {
      return;
    }
    const userToken = this.dataService.getUserToken();
    const isSubscriber = !!sessionStorage.getItem('subscriberLogin');
    const link = '/view_report/' + applicationId + '/';
    const set3 = '#';
    const queryParams: any = {
      id: applicationId,
      token: userToken,
      isSubscriber: isSubscriber ? 'true' : 'false',
      name: searchName || 'full',
      view: 'true'
    };
    if (subscriberId) {
      queryParams.subscriberID = subscriberId;
    }
    if (applicantId) {
      queryParams.applicantID = applicantId;
    }

    try {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([link], { queryParams })
      );
      const windowFeatures = 'width=1000,height=800,scrollbars=yes,resizable=yes';
      const newWindow = window.open(encodeURI(set3) + url, '_blank', windowFeatures);
      if (newWindow) {
        newWindow.focus();
        return;
      }
    } catch {
      // ignore popup blocker error
    }

    this.navigate(link, queryParams);
  }

  openGroupReportViewer(applicationId: any, subscriberId?: any, applicantId?: any) {
    if (!applicationId) {
      return;
    }
    const userToken = this.dataService.getUserToken();
    const isSubscriber = !!sessionStorage.getItem('subscriberLogin');
    const link = '/qa-group-report/' + applicationId + '/';
    const set3 = '#';
    const queryParams: any = {
      id: applicationId,
      token: userToken,
      isSubscriber: isSubscriber ? 'true' : 'false',
      name: 'full'
    };
    if (subscriberId) {
      queryParams.subscriberID = subscriberId;
    }
    if (applicantId) {
      queryParams.applicantId = applicantId;
    }

    try {
      const url = this.router.serializeUrl(
        this.router.createUrlTree([link], { queryParams })
      );
      const windowFeatures = 'width=1000,height=800,scrollbars=yes,resizable=yes';
      const newWindow = window.open(encodeURI(set3) + url, '_blank', windowFeatures);
      if (newWindow) {
        newWindow.focus();
        return;
      }
    } catch {
      // ignore
    }

    this.navigate(link, queryParams);
  }

  /**
   * The search API's action/status doesn't reliably say which queue an
   * application is currently in, so ask the QA, Error and Completed list APIs
   * and open the first queue that actually contains it.
   */
  openInQueue(applicationId: any, fallbackPath: string) {
    const query = { gsId: applicationId };
    if (!applicationId) {
      this.navigate(fallbackPath, query);
      return;
    }
    const token = this.dataService.getUserToken();
    const queues = [
      { path: '/qa', queue: 'qa', endpoint: 'admin/quality_assurence/newlist ' },
      { path: '/qa/errors', queue: 'error', endpoint: 'admin/quality_assurence/errorList' },
      { path: '/qa/completed', queue: 'completed', endpoint: 'admin/quality_assurence/completedList' }
    ];
    const lookups = queues.map((q) =>
      this.dataService.getAllQAList(q.endpoint, {
        tags: [], modules: [], pageSize: 10, pageNum: 1,
        searchKey: String(applicationId), queue: q.queue, fetchedAt: Date.now()
      }, token).pipe(catchError(() => of(null)))
    );
    this.ngxLoader.start();
    forkJoin(lookups).subscribe((results: any[]) => {
      this.ngxLoader.stop();
      const index = results.findIndex((res: any) => {
        const data = res && res.Status === 200 ? res.Data : null;
        const rows = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        return rows.some((r: any) => String(r.id ?? r.applicationId ?? r.applicationID) === String(applicationId));
      });
      this.navigate(index !== -1 ? queues[index].path : fallbackPath, query);
    }, () => {
      this.ngxLoader.stop();
      this.navigate(fallbackPath, query);
    });
  }

  /** Applications with a final decision / complete status live in the Completed queue, not the QA queue. */
  private isCompletedStatus(item?: GlobalSearchItem): boolean {
    const entity: any = (item && item.entity) || {};
    const status = String((item && item.status) || entity.status || entity.appStatus || entity.AppStatus || '').toLowerCase();
    return /complete|accept|approv|reject|denied|declin/.test(status);
  }

  private isSubscriberSession(): boolean {
    return !!sessionStorage.getItem('subscriberLogin');
  }

  private openSubscriberReportList(applicationId: any) {
    this.navigate('/subscriber-reports/report-list', { id: applicationId });
  }

  private openProperty(propertyId: any, subscriberId?: any, companyName?: string) {
    if (this.isSubscriberSession()) {
      this.navigate('/manageProperties', { propertyId, subscriberId });
    } else {
      this.navigate('/subscribers/manage-properties', { name: companyName, propertyId, subscriberId });
    }
  }

  getReturnToQueueLabel(searchName: string): string {
    return isAllowedVendorReport(searchName || '') ? 'Return to Vendor Queue' : 'Return to QA';
  }

  returnToQa(payload: any, item?: GlobalSearchItem) {
    const searchName = payload.searchName || (item && item.entity && item.entity.searchName) || '';
    const body: any = {
      applicationID: payload.applicationID || payload.applicationId || (item && item.entity && item.entity.applicationId),
      subscriberID: payload.subscriberID || payload.subscriberId || (item && item.entity && item.entity.subscriberId),
      applicantID: payload.applicantID || payload.applicantId || (item && item.entity && item.entity.applicantId),
      reportID: payload.reportID || payload.reportId || (item && item.entity && item.entity.reportId)
    };
    try {
      const systemData1 = this.dataService.getSystemData();
      const systemData = systemData1 ? JSON.parse(systemData1) : {};
      body.location = systemData.location;
      body.ipAddress = systemData.ipAddress;
      body.device = systemData.deviceType;
      body.os_version = systemData.os_version;
    } catch {
      // optional metadata
    }

    const token = this.dataService.getUserToken();
    this.ngxLoader.start();
    this.dataService.addReleaseQANote(
      'admin/quality_assurence/reprocessIntoQAList',
      body,
      token
    ).subscribe((res: any) => {
      this.ngxLoader.stop();
      if (res && res.Status === 200) {
        const msg = isAllowedVendorReport(searchName)
          ? 'Successfully Returned to Vendor Queue'
          : 'Successfully Returned to QA';
        this.mainService.ShowAlert('Success', msg);
        try {
          window.dispatchEvent(new CustomEvent('ps-global-search-returned-to-qa', { detail: body }));
        } catch {
          // ignore
        }
      } else {
        this.mainService.ShowAlert('Error', 'Something went wrong. Please try after sometime');
      }
    }, () => {
      this.ngxLoader.stop();
      this.mainService.ShowAlert('Error', 'Something went wrong. Please try after sometime');
    });
  }

  /**
   * Admin-only destinations that either bounce a subscriber session back to
   * the dashboard (they carry their own `subscriberLogin` redirect guard) or
   * render blank for a subscriber (they expect admin-only data). Every
   * navigation funnels through navigate(), regardless of whether the path
   * came from a static page suggestion, a backend action payload, or the
   * type-based fallback, so rewriting it here is the one place that covers
   * every entry point.
   */
  private static readonly SUBSCRIBER_ROUTE_REWRITES: Record<string, string> = {
    '/qa': '/subscriber-reports/report-list',
    '/qa/errors': '/subscriber-reports/report-list',
    '/qa/completed': '/subscriber-reports/report-list',
    '/users/manageusers': '/subscriber_users/managesubscriberuser',
    '/add-subscriber': '/subscriber_edit_profile',
    '/subscribers/manage-subscriber': '/subscriber_edit_profile',
    '/invoice-list': '/subscriber-manage-payment',
    '/admin-settings': '/subscriber-settings/email-settings',
    '/report-management': '/subscriber-reports/report-list'
  };

  public navigate(path: string, query?: Record<string, any>) {
    if (!path) {
      return;
    }
    const cleanQuery: any = {};
    if (query) {
      Object.keys(query).forEach((key) => {
        if (query[key] !== undefined && query[key] !== null && query[key] !== '') {
          cleanQuery[key] = query[key];
        }
      });
    }

    let targetPath = String(path).trim();
    if (targetPath.startsWith('#/')) {
      targetPath = targetPath.substring(1);
    } else if (targetPath.startsWith('#')) {
      targetPath = targetPath.substring(1);
    }
    if (!targetPath.startsWith('/')) {
      targetPath = '/' + targetPath;
    }

    if (this.isSubscriberSession() && GlobalSearchActionsService.SUBSCRIBER_ROUTE_REWRITES[targetPath]) {
      targetPath = GlobalSearchActionsService.SUBSCRIBER_ROUTE_REWRITES[targetPath];
    }

    const qIndex = targetPath.indexOf('?');
    if (qIndex !== -1) {
      const qs = targetPath.substring(qIndex + 1);
      targetPath = targetPath.substring(0, qIndex);
      const searchParams = new URLSearchParams(qs);
      searchParams.forEach((val, k) => {
        if (cleanQuery[k] === undefined) {
          cleanQuery[k] = val;
        }
      });
    }

    this.navigateFresh(targetPath, cleanQuery);
  }

  /**
   * Angular's default RouteReuseStrategy keeps the same component instance
   * alive when navigating to a route that's already active, only updating its
   * ActivatedRoute query params. Most destination pages only read query
   * params once (in ngOnInit), so picking a second search result for the same
   * page (e.g. another property while already on Manage Properties) silently
   * no-ops instead of showing the new result. Forcing a one-off "don't reuse"
   * makes the destination reliably reload. This is scoped to the leaf route
   * only (future/curr have no further children) so ancestor routes — notably
   * the top-level LayoutComponent that hosts the header/global search box —
   * keep being reused and aren't torn down along with it.
   */
  private navigateFresh(targetPath: string, queryParams: Record<string, any>) {
    const strategy: any = this.router.routeReuseStrategy;
    const original = strategy.shouldReuseRoute.bind(strategy);
    strategy.shouldReuseRoute = (future: any, curr: any) => {
      if (!future.children.length && !curr.children.length) {
        return false;
      }
      return original(future, curr);
    };
    const restore = () => { strategy.shouldReuseRoute = original; };
    this.router.navigate([targetPath], { queryParams }).then(restore, restore);
  }

  private switchSubscriber(subscriberId: any, companyName?: string) {
    if (!subscriberId) {
      return;
    }
    const subscriberLogin = {
      Data: { id: subscriberId, role: 'admin' },
      tableName: 'txnUsers',
      companyName: companyName || ''
    };
    sessionStorage.setItem('subscriberLogin', JSON.stringify(subscriberLogin));
    this.ngxLoader.start();
    this.router.navigateByUrl('/home');
    setTimeout(() => {
      window.location.reload();
    }, 400);
  }
}
